/**
 * Live pop-ups in the corner while the client is in the app: fired alerts, and notifications as they
 * arrive (they also land under the bell). Margin and stop-out stay until closed; the rest close
 * themselves, with a bar running down.
 */
import type { CSSProperties } from 'react';
import { stripEmoji } from '@platform/shared';
import { Link } from '../lib/router';
import { dismissToast, useAlertToasts } from '../stores/alerts';
import { dismissNotificationToast, isUrgent, markRead, TOAST_MS, useNotificationToasts } from '../stores/notifications';
import { ctaLabel, NotificationIcon, timeLabel } from './NotificationIcon';
import { cx, Icon } from './ui';

interface Toast {
  id: string;
  title: string;
  body: string;
  link: string | null;
  category: string | null;
  at: string;
  urgent: boolean;
  open: () => void;
  close: () => void;
}

export function AlertToasts() {
  const alerts = useAlertToasts();
  const notes = useNotificationToasts();
  const toasts: Toast[] = [
    ...notes.map((n) => ({
      id: n.id,
      title: n.title,
      body: n.body,
      link: n.link,
      category: n.category,
      at: n.createdAt,
      urgent: isUrgent(n),
      // The link itself navigates; opening it reads the notification.
      open: () => {
        void markRead([n.id]);
        dismissNotificationToast(n.id);
      },
      close: () => dismissNotificationToast(n.id),
    })),
    ...alerts.map((a) => ({
      id: a.id,
      title: stripEmoji(a.title),
      body: stripEmoji(a.body),
      link: a.link,
      category: 'market.alert',
      at: a.firedAt,
      urgent: false,
      open: () => dismissToast(a.id),
      close: () => dismissToast(a.id),
    })),
  ].sort((a, b) => b.at.localeCompare(a.at));
  if (!toasts.length) return null;

  return (
    <div aria-live="polite" className="pointer-events-none fixed right-4 bottom-[calc(env(safe-area-inset-bottom)+96px)] z-[60] flex w-[min(372px,calc(100vw-32px))] flex-col gap-2.5 lg:bottom-12">
      {toasts.map((t) => (
        <div
          key={t.id}
          role="status"
          className={cx('shadow-pop sheet-in pointer-events-auto overflow-hidden rounded-[14px] border bg-float', t.urgent ? 'border-fg' : 'border-float-line')}
        >
          <div className="flex items-start gap-3 py-3.5 pr-2.5 pl-3.5">
            <NotificationIcon n={{ category: t.category, kind: 'info' }} size="sm" />
            <div className="flex min-w-0 flex-1 flex-col gap-[3px] pt-px">
              <div className="flex items-baseline gap-2">
                <span className="text-[13.5px] font-semibold">{t.title}</span>
                <span className="num shrink-0 text-[11px] text-muted">{timeLabel(t.at)}</span>
              </div>
              {t.body && <div className="text-[12.5px] leading-[1.45] text-muted">{t.body}</div>}
              {t.link && (
                <div className="mt-2 flex gap-2">
                  <Link
                    to={t.link}
                    onClick={t.open}
                    className={cx('inline-flex h-8 items-center rounded-full px-3 text-[12.5px] font-semibold no-underline', t.urgent ? 'bg-fg text-bg' : 'bg-pill text-fg')}
                  >
                    {ctaLabel({ category: t.category, link: t.link })}
                  </Link>
                </div>
              )}
            </div>
            <button type="button" aria-label="Dismiss" onClick={t.close} className="grid size-8 shrink-0 place-items-center rounded-lg text-muted hover:text-fg">
              <Icon name="close" size={14} />
            </button>
          </div>
          {!t.urgent && (
            <div aria-hidden="true" className="h-[3px] bg-line">
              <div className="toast-timer h-full bg-fg" style={{ '--toast-ms': `${TOAST_MS}ms` } as CSSProperties} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
